import { Component, OnInit, Input } from '@angular/core';
import { PressureTemperatureCellViewModel, PressureTemperatureTableViewModel } from '../../shared/models/autogenerated-piping';
@Component({
    selector: 'app-pressure-temp-table',
    templateUrl: './pressure-temp-table.component.html',
    styleUrls: ['./pressure-temp-table.component.scss'],
    standalone: false
})
export class PressureTempTableComponent implements OnInit {
  @Input() presTempTables: PressureTemperatureTableViewModel[];
  @Input() pipeClasTemperatureUnits: string;
  @Input() pipeClassPressureUnits: string;
  @Input() fromRevisionName: string; // For displaying in REV column when changed
  @Input() hideAdditionalNotes: boolean = false;
  @Input() hideCommonNotes: boolean = false;
  @Input() hideRelationNotes: boolean = false;
  @Input() isRevisionInProgress: boolean = false; // Whether the revision is being edited (not published)

  sourceOrAdditionalNoteExists: boolean = false;

  getLastSegment(revisionName: string | undefined): string {
    if (!revisionName || revisionName.trim() === '') return '';
    const segments = revisionName.split('-');
    return segments[segments.length - 1] || '';
  }

  getPTTRevision(table: PressureTemperatureTableViewModel): string {
    const lastUpdatedRevision: string = table.lastUpdatedRevision || '';
    if (this.isRevisionInProgress) {      
      // Revision is in progress: only show if the table was changed in this revision
      if (table.changed) {
        return this.getLastSegment(this.fromRevisionName);
      }

      return '';
    }

    // Published/viewing mode: show lastUpdatedRevision if available
    if (lastUpdatedRevision) {
      return this.getLastSegment(lastUpdatedRevision);
    }
    return '';
  }

  ngOnInit(): void {
    this.sortTableCellsByTemp();
    this.sourceOrAdditionalNoteExists = this.setSourceOrAdditionalNoteExists();
  }

  // Sorts the PTT's cells by temperature in ascending order
  private sortTableCellsByTemp(): void {
    for (const table of this.presTempTables) {
      table.cells.sort((a, b) => {
        if (a.temperature > b.temperature) {
          return 1;
        }
        if (a.temperature < b.temperature) {
          return -1;
        }
        return 0;
      });
    }
  }

  private setSourceOrAdditionalNoteExists(): boolean {
    this.presTempTables.forEach(ptt => {
      ptt.relationAdditionalNotes.forEach(rn => {
        if (rn.type === 'source' || rn.type === 'additional') {
          return true;
        }
      });
    });
    return false;
  }

  filterOnlySpecificPressureUnits(cellsToFilter: PressureTemperatureCellViewModel[], unit: string): PressureTemperatureCellViewModel[] {
    if (!cellsToFilter || !unit) {
      return [];
    }

    const filtered = cellsToFilter.filter(cell => cell?.pressureUnit?.toLowerCase() === unit?.toLowerCase());
    return filtered;
  }

  filterOnlySpecificTemperatureUnits(cellsToFilter: PressureTemperatureCellViewModel[], unit: string): PressureTemperatureCellViewModel[] {
    if (!cellsToFilter || !unit) {
      return [];
    }

    const filtered = cellsToFilter.filter(cell => cell?.temperatureUnit?.toLowerCase() === unit?.toLowerCase());
    return filtered;
  }


  getGroupedCells(cells: PressureTemperatureCellViewModel[]): {
    usCustomaryCells: PressureTemperatureCellViewModel[],
    internationalSystemCells: PressureTemperatureCellViewModel[]
  } {
    if (!cells || cells.length === 0) {
      return { usCustomaryCells: [], internationalSystemCells: [] };
    }

    const usCustomaryCells = cells.filter(cell =>
      cell.isUSCustomary === true ||
      (cell.temperatureUnit?.toLowerCase() === 'f' && cell.pressureUnit?.toLowerCase() === 'psig')
    ).sort((a, b) => a.temperature - b.temperature);

    const internationalSystemCells = cells.filter(cell =>
      cell.isInternationalSystem === true ||
      (cell.temperatureUnit?.toLowerCase() === 'c' && cell.pressureUnit?.toLowerCase() === 'kpag')
    ).sort((a, b) => a.temperature - b.temperature);

    return { usCustomaryCells, internationalSystemCells };
  }

  getDisplayCells(cells: PressureTemperatureCellViewModel[]): Array<{
    fahrenheit: number,
    psig: number,
    celsius: number,
    kPag: number,
    isAsteriskRequired: boolean
  }> {
    if (!cells || cells.length === 0) {
      return [];
    }

    const { usCustomaryCells, internationalSystemCells } = this.getGroupedCells(cells);
    const hasInternationalSystem = internationalSystemCells.length > 0;
    const hasUSCustomary = usCustomaryCells.length > 0;

    if (!hasUSCustomary && !hasInternationalSystem) {
      return [];
    }

    const baseCells = hasUSCustomary ? usCustomaryCells : internationalSystemCells;
    const displayData: Array<{ fahrenheit: number, psig: number, celsius: number, kPag: number, isAsteriskRequired: boolean }> = [];

    for (let i = 0; i < baseCells.length; i++) {
      let fahrenheit: number;
      let psig: number;
      let celsius: number;
      let kPag: number;
      let isAsteriskRequired: boolean;

      if (hasUSCustomary) {
        const usCell = usCustomaryCells[i];
        fahrenheit = usCell.temperature;
        isAsteriskRequired = usCell.isAsteriskRequired;
        psig = usCell.pressure;

        if (hasInternationalSystem && internationalSystemCells[i]) {
          const intCell = internationalSystemCells[i];
          celsius = intCell.temperature;
          kPag = intCell.pressure;
        } else {
          celsius = this.fahrenheitToCelsius(fahrenheit);
          kPag = this.psigToKPag(psig);
        }
      } else {
        const intCell = internationalSystemCells[i];
        celsius = intCell.temperature;
        isAsteriskRequired = intCell.isAsteriskRequired;
        kPag = intCell.pressure;
        fahrenheit = this.celsiusToFahrenheit(celsius);
        psig = this.kPagToPsig(kPag);
      }

      displayData.push({ fahrenheit, psig, celsius, kPag, isAsteriskRequired });
    }
    return displayData;
  }

  private fahrenheitToCelsius(fahrenheit: number): number {
    return (fahrenheit - 32) * 5 / 9;
  }

  private celsiusToFahrenheit(celsius: number): number {
    return (celsius * 9/5) + 32;
  }

  private psigToKPag(psig: number): number {
    return Math.round(psig * 6.894757 / 5) * 5;
  }

  private kPagToPsig(kPag: number): number {
    return kPag / 6.894757;
  }

  formatTemperature(temp: number): string {
    const rounded = Math.round(temp * 10) / 10;
    if (rounded === Math.floor(rounded)) {
      return rounded.toFixed(0);
    }
    return rounded.toFixed(1);
  }

  getTemperatureInFahrenheit(cell: PressureTemperatureCellViewModel): number {
    if (cell.temperatureUnit?.toLowerCase() === 'f') {
      return Math.round(cell.temperature);
    } else if (cell.temperatureUnit?.toLowerCase() === 'c') {
      return Math.round((cell.temperature * 9/5) + 32);
    }
    return Math.round(cell.temperature);
  }

  getPressureInPsig(cell: PressureTemperatureCellViewModel): number {
    if (cell.pressureUnit?.toLowerCase() === 'psig') {
      return Math.round(cell.pressure);
    } else if (cell.pressureUnit?.toLowerCase() === 'kpag') {
      return Math.round(cell.pressure * 0.145038);
    }
    return Math.round(cell.pressure);
  }

  getTemperatureInUnit(cell: PressureTemperatureCellViewModel, unit: string): number {
    if (unit?.toLowerCase() === 'c') {
      if (cell.temperatureUnit?.toLowerCase() === 'c') {
        return Math.round(cell.temperature);
      } else if (cell.temperatureUnit?.toLowerCase() === 'f') {
        return Math.round((cell.temperature - 32) * 5/9);
      }
    }
    return Math.round(cell.temperature);
  }

  getPressureInUnit(cell: PressureTemperatureCellViewModel, unit: string): number {
    if (unit?.toLowerCase() === 'kpag') {
      if (cell.pressureUnit?.toLowerCase() === 'kpag') {
        return Math.round(cell.pressure);
      } else if (cell.pressureUnit?.toLowerCase() === 'psig') {
        return Math.round(cell.pressure * 6.89476);
      }
    }
    return Math.round(cell.pressure);
  }

  getRowSpan(table: PressureTemperatureTableViewModel): number {
    let rowCount = 2;
    if (this.pipeClasTemperatureUnits && this.pipeClasTemperatureUnits !== 'F') {
      rowCount++;
    }
    rowCount++;

    return rowCount;
  }

  openCommonNote(code) {
    alert('Details of Common Note with code: ' + code);
  }

  /**
   * Filter notes by type (NpsRangeInfo, PtrNote, ServiceNote, Additional, etc.)
   */
  getNotesOfType(notes: any[], type: string): any[] {
    if (!notes || notes.length === 0) {
      return [];
    }

    // Case-insensitive comparison to handle different casing from backend
    return notes.filter(note => note.type?.toLowerCase() === type.toLowerCase());
  }
}
